<script>
import cloneDeep from 'lodash/cloneDeep'
import lazyLoadComponent from '@router/lazyload-component'
import { isTempleModule, isLightModule } from '@utils'

import Layout from '@layouts/main'

export default {
  page: {
    title: '編輯項目',
    meta: [{ name: 'description', content: 'Edit Product' }],
  },
  components: {
    Layout,
  },
  props: {
    defaultValue: {
      type: Object,
      required: true,
    },
    productType: {
      type: String,
      default: 'activity',
    },
  },
  data() {
    return {
      tryingToSave: false,
      saveButtonVisible: true,
      activeTab: '1',
      tabListLight: [
        {
          title: '基本資訊',
          component: () => lazyLoadComponent(import('./components/ibobee/StepBasic')),
          showFooter: true,
        },
        {
          title: '年份與燈種',
          component: () => lazyLoadComponent(import('./components/ibobee/StepYearAndLight')),
          showFooter: false,
        },
        {
          title: '自訂題目',
          component: () => lazyLoadComponent(import('./components/ibobee/StepExtraField')),
          showFooter: true,
        },
      ],
      componentData: this.defaultValue,
    }
  },
  computed: {
    productId() {
      return this.$route.params.productId
    },
    isLightModule() {
      return isLightModule(this.productType)
    },
    tabListBasic() {
      return [
        {
          title: '基本資訊',
          component: () => {
            if (isTempleModule(this.productType)) return lazyLoadComponent(import('./components/ibobee/StepBasic'))
            return lazyLoadComponent(import('./components/Step1Basic'))
          },
          showFooter: true,
        },
        {
          title: '時段',
          component: () => lazyLoadComponent(import('./components/Step2InventoryGroupList')),
          showFooter: false,
        },
        {
          title: '購買方案',
          component: () => lazyLoadComponent(import('./components/Step3Price')),
          showFooter: true,
        },
        {
          title: '行事曆',
          component: () => lazyLoadComponent(import('./components/Step2InventoryCalendar')),
          showFooter: false,
        },
        {
          title: '自訂題目',
          component: () => {
            if (isTempleModule(this.productType)) return lazyLoadComponent(import('./components/ibobee/StepExtraField'))
            return lazyLoadComponent(import('./components/Step4ExtraField'))
          },
          showFooter: true,
        },
      ]
    },
    tabList() {
      if (this.isLightModule) return this.tabListLight
      return this.tabListBasic
    },
  },
  methods: {
    handleFormValidate(valid) {
      this.saveButtonVisible = valid
    },
    handleStorePrice(storeData) {
      storeData.forEach((data) => {
        if (!!data.saleStartDate) data.saleStartDate = data.saleStartDate.split(':00')[0]
        if (!!data.saleEndDate) data.saleEndDate = data.saleEndDate.split(':00')[0]
        if (data.itemId === null) delete data.itemId
      })
      return this.$store.dispatch('product/storePrices', {
        productId: this.productId,
        storeData,
      })
    },
    handleStoreProduct(submitData) {
      const storeData = {
        ...submitData,
        type: this.productType,
        staffs: submitData.staffs.map((staff) => {
          return {
            id: staff.id,
            hostTitle: staff.hostTitle,
          }
        }),
      }
      return this.$store.dispatch('product/store', storeData)
    },
    handleStoreExtraField(submitData) {
      const extraFields = [...submitData.byOrder, ...submitData.bySize]
      const storeData = {
        productId: this.productId,
      }
      if (extraFields.length > 0) storeData.extraFields = extraFields
      return this.$store.dispatch('product/updateExtraField', storeData)
    },
    getProductInfo() {
      this.$store.dispatch('product/fetchSingle', this.productId).then((product) => {
        this.componentData = cloneDeep(product)
        // this.$route.params.product = cloneDeep(product)
      })
    },
    async handleSaveSuccess() {
      await this.$alert('儲存成功', '太棒了！', {
        confirmButtonText: '確認',
        type: 'success',
        center: true,
      })
      this.tryingToSave = false
    },
    async onClickSave() {
      this.tryingToSave = true
      try {
        switch (+this.activeTab) {
          case 1:
            const step1Ref = this.$refs.step1[0]
            await step1Ref.validate()
            const formData = step1Ref.submitData
            await this.handleStoreProduct(formData)
            this.handleSaveSuccess()
            break
          case 3:
            const { params } = this.$refs.step3[0]
            await this.handleStorePrice(params)
            this.handleSaveSuccess()
            break
          case 5:
            const list = this.$refs.step5[0].list
            await this.handleStoreExtraField(list)
            await this.getProductInfo()
            this.handleSaveSuccess()
            break
        }
      } catch (error) {
        this.tryingToSave = false
      }
    },
    async onClickSaveLight() {
      this.tryingToSave = true
      try {
        switch (+this.activeTab) {
          case 1:
            const step1Ref = this.$refs.step1[0]
            await step1Ref.validate()
            const formData = step1Ref.submitData
            await this.handleStoreProduct(formData)
            this.handleSaveSuccess()
            break
          case 3:
            const list = this.$refs.step3[0].list
            await this.handleStoreExtraField(list)
            await this.getProductInfo()
            this.handleSaveSuccess()
            break
        }
      } catch (error) {
        this.tryingToSave = false
      }
    },
    onClickLeave() {
      this.$confirm(
        this.$t('PRODUCT.LEAVE_EDIT_CONFIRM_MESSAGE', {
          name: this.$t(`PRODUCT.${this.productType}.NAME`),
        }),
        '您將離開此頁面',
        {
          confirmButtonText: '確定',
          cancelButtonText: '取消',
          type: 'warning',
          center: true,
          showClose: false,
        }
      ).then(() => {
        this.$router.push({ name: 'products' })
      })
    },
  },
}
</script>

<template>
  <Layout :class="$style.page">
    <BasePageHeader
      :title="
        $t('GLOBAL.HEADER_EDIT_PREFIX', {
          name: $t(`PRODUCT.${productType}.NAME`),
          item: defaultValue.name,
        })
      "
    ></BasePageHeader>
    <el-tabs v-model="activeTab" type="border-card">
      <el-tab-pane v-for="(tab, index) in tabList" :key="index" :label="tab.title" :name="`${index + 1}`">
        <keep-alive>
          <component :is="tab.component" :ref="`step${index + 1}`" :class="$style['card-wrapper']" :item-type="productType" :default-value="componentData" @validate="handleFormValidate"></component>
        </keep-alive>

        <div v-show="tab.showFooter" :class="$style.footer">
          <el-button @click="onClickLeave">取消編輯</el-button>
          <el-button v-if="isLightModule" v-show="saveButtonVisible" type="primary" :loading="tryingToSave" @click="onClickSaveLight">儲存變更</el-button>
          <el-button v-else v-show="saveButtonVisible" type="primary" :loading="tryingToSave" @click="onClickSave">儲存變更</el-button>
        </div>
      </el-tab-pane>
    </el-tabs>
  </Layout>
</template>

<style lang="scss" module>
@import '@src/design/layout_detail_steps.scss';
</style>
